/** @jsx jsx */
import { Dialog } from '@datacamp/waffles/dialog';
import { ChevronRight } from '@datacamp/waffles/icon';
import { tokens } from '@datacamp/waffles/tokens';
import { css, jsx } from '@emotion/react';
import React, { useCallback } from 'react';

import { useDispatch, useSelector } from '../../interfaces/State';
import { showModal } from '../../redux/actions';
import { MODAL_TYPE } from '../../redux/selectors';
import { selectCourseDatasets } from '../../redux/selectors/course';

const listItemStyle = {
  borderTop: `${tokens.borderWidth.thin} solid ${tokens.colors.transparentGrey}`,
  ':last-of-type': {
    borderBottom: `${tokens.borderWidth.thin} solid ${tokens.colors.transparentGrey}`,
    marginBottom: tokens.spacing.large,
  },
};

const buttonStyle = {
  width: '100%',
  height: tokens.sizing.medium,
  display: 'flex',
  gap: tokens.spacing.medium,
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: 'transparent',
  fontFamily: tokens.fontFamilies.sansSerif,
  lineHeight: tokens.lineHeights.default,
  fontSize: tokens.fontSizes.medium,
  border: 0,
  paddingLeft: tokens.spacing.large,
  paddingRight: tokens.spacing.large,
  transition: `background-color 125ms ease-out`,
  ':hover': {
    backgroundColor: tokens.colors.transparentGreySubtle,
  },
  ':focus-visible': {
    outline: `${tokens.borderWidth.medium} solid ${tokens.colors.blueDark}`,
    outlineOffset: -2,
  },
};

const GuidesModal: React.FC = () => {
  const dispatch = useDispatch();
  const onShowDownloadDatasets = useCallback(
    () => dispatch(showModal({ modal: MODAL_TYPE.DOWNLOAD_DATASETS })),
    [dispatch],
  );
  const onShowReferenceMaterial = useCallback(
    () => dispatch(showModal({ modal: MODAL_TYPE.REFERENCE_MATERIAL })),
    [dispatch],
  );
  const onShowInteractWithFloatingPane = useCallback(
    () =>
      dispatch(showModal({ modal: MODAL_TYPE.INTERACT_WITH_FLOATING_PANE })),
    [dispatch],
  );
  const onShowScreenLarger = useCallback(
    () => dispatch(showModal({ modal: MODAL_TYPE.SCREEN_LARGER })),
    [dispatch],
  );
  const onShowLoadingWorkbooks = useCallback(
    () => dispatch(showModal({ modal: MODAL_TYPE.LOADING_WORKBOOKS })),
    [dispatch],
  );

  const datasetsEmpty = useSelector(selectCourseDatasets).isEmpty();

  return (
    <>
      <Dialog.Header>Guides</Dialog.Header>
      <ul
        css={css`
          list-style: none;
          padding: 0;
          margin: 0;
        `}
      >
        <li css={listItemStyle}>
          <button
            css={buttonStyle}
            data-cy="loading-workbooks-modal-button"
            onClick={onShowLoadingWorkbooks}
          >
            Loading workbooks in the Remote Desktop <ChevronRight />
          </button>
        </li>
        <li css={listItemStyle}>
          <button
            css={buttonStyle}
            data-cy="interactive-floating-pane-modal-button"
            onClick={onShowInteractWithFloatingPane}
          >
            {'Interacting with the floating instruction pane '}
            <ChevronRight />
          </button>
        </li>
        <li css={listItemStyle}>
          <button
            css={buttonStyle}
            data-cy="screen-larger-modal-button"
            onClick={onShowScreenLarger}
          >
            Making the screen larger <ChevronRight />
          </button>
        </li>
        <li css={listItemStyle}>
          <button
            data-cy="reference-material-modal-button"
            css={buttonStyle}
            onClick={onShowReferenceMaterial}
          >
            Accessing the reference material <ChevronRight />
          </button>
        </li>
        <li css={listItemStyle}>
          <button
            data-cy="download-datasets-modal-button"
            disabled={datasetsEmpty}
            onClick={onShowDownloadDatasets}
            css={buttonStyle}
          >
            Download Datasets <ChevronRight />
          </button>
        </li>
      </ul>
    </>
  );
};

export default GuidesModal;
